import { Box, BoxProps, Tooltip, TooltipProps, useOutsideClick } from '@chakra-ui/react'
import React, { memo, MouseEvent, MouseEventHandler, useRef, useState } from 'react'

export type CustomTooltipProps = {
  trigger?: 'click' | 'hover' | 'clickHover'
  propagateClick?: boolean
  boxProps?: BoxProps
  isDisabled?: boolean
} & TooltipProps

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  trigger = 'hover',
  children,
  propagateClick,
  isDisabled,
  boxProps,
  ...tooltipProps
}) => {
  const [isTooltipOpen, setTooltipOpen] = useState(false)
  const openedByClick = useRef(false)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const handleButtonClick: MouseEventHandler<HTMLDivElement> = (e: MouseEvent<HTMLDivElement>) => {
    if (!isTooltipOpen || !openedByClick.current) {
      setTooltipOpen(true)
      openedByClick.current = true
    } else {
      setTooltipOpen(false)
      openedByClick.current = false
    }
    if (!propagateClick) e.stopPropagation()
  }

  const handleMouseEnter = () => {
    if (!openedByClick.current) {
      setTooltipOpen(true)
    }
  }

  const handleMouseLeave = () => {
    if (!openedByClick.current) {
      setTooltipOpen(false)
    }
  }

  useOutsideClick({
    ref: tooltipRef,
    handler: (event) => {
      if (tooltipRef.current) {
        const describedByElement = document.querySelector(
          `[aria-describedby="${tooltipRef.current.id}"]`,
        )
        if (describedByElement) {
          const clickedElementIsInsideTooltip = tooltipRef.current?.contains(event.target as Node)
          const clickedElementIsTooltipDescription =
            describedByElement === event.target || describedByElement.contains(event.target as Node)

          if (
            openedByClick.current &&
            !clickedElementIsTooltipDescription &&
            !clickedElementIsInsideTooltip
          ) {
            setTooltipOpen(false)
            openedByClick.current = false
          }
        }
      }
    },
  })

  const eventHandlers = isDisabled
    ? {}
    : trigger === 'hover'
      ? {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        }
      : trigger === 'clickHover'
        ? {
            onClick: handleButtonClick,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          }
        : trigger === 'click'
          ? {
              onClick: handleButtonClick,
            }
          : {}

  return (
    <Tooltip
      ref={tooltipRef}
      isOpen={isTooltipOpen}
      pointerEvents='auto'
      maxW='100%'
      closeOnClick={false}
      closeOnEsc={false}
      closeOnPointerDown={false}
      {...tooltipProps}
    >
      <Box {...eventHandlers} {...boxProps}>
        {children}
      </Box>
    </Tooltip>
  )
}

export default memo(CustomTooltip)
